import _keyBy from 'lodash/keyBy';
import _groupBy from 'lodash/groupBy';
import _uniq from 'lodash/uniq';
import _every from 'lodash/every';
import { canadianProvinces, americanStates, countries, allLocations } from 'constants/regions';
import i18n from '../i18n';

const subregionsByValue = _keyBy([...canadianProvinces, ...americanStates], 'value');
const countriesByValue = _keyBy(countries, 'value');

function getLocationName(location) {
  return countriesByValue[location]?.label ?? subregionsByValue[location]?.label ?? '';
}

export const getLocations = (locations = []) => {
  if (locations === null || !locations.length) return { summary: '' };
  let region = '';
  const [firstCountryCode, firstSubregion] = locations[0].split('.');
  if (_every(locations, l => l.startsWith(firstCountryCode))) {
    region = getLocationName(firstCountryCode);
  }
  if (locations.length === 1) {
    return { summary: `${firstSubregion ? `${getLocationName(locations[0])}, ` : ''}${region}` };
  }
  return {
    summary: `${i18n.t('opportunities.project.multiple_locations')}${region ? `, ${region}` : ''}`,
    extended: locations.map(getLocationName).filter(Boolean).join(', '),
  };
};

const countriesIndexed = _keyBy(countries, 'RegionID');
const subregionsById = _keyBy([...canadianProvinces, ...americanStates], 'SubregionID');
const subregionsGrouped = _groupBy([...canadianProvinces, ...americanStates], 'RegionID');

export const regionDataToLocations = regionData => {
  if (regionData === null) {
    return allLocations;
  }
  const result = [];
  const { regions = [], subregions = [], partialRegions = [] } = regionData;
  regions.forEach(regionId => {
    if (countriesIndexed[regionId]) {
      result.push(countriesIndexed[regionId].value);
    }
    if (subregionsGrouped[regionId]) {
      result.push(...subregionsGrouped[regionId].map(({ value }) => value));
    }
  });
  subregions.forEach(subregionId => {
    if (subregionsById[subregionId]) {
      result.push(subregionsById[subregionId].value);
    }
  });
  partialRegions.forEach(regionId => {
    if (countriesIndexed[regionId]) {
      result.push(countriesIndexed[regionId].value);
    }
  });
  return _uniq(result);
};
export const locationsToRegionData = locations => {
  const processedLocations = locations.reduce((prev, location) => {
    const [countryCode, subregionCode] = location.split('.');
    const { RegionID } = countriesByValue[countryCode];
    prev[RegionID] = prev[RegionID] || {
      subregions: [],
      partialRegion: false,
      region: false,
    };
    if (!subregionCode) {
      prev[RegionID].partialRegion = true;
    } else {
      prev[RegionID].subregions.push(subregionsByValue[location].SubregionID);
    }
    if (
      prev[RegionID].partialRegion &&
      (!subregionsGrouped[RegionID] ||
        prev[RegionID].subregions.length >= subregionsGrouped[RegionID].length)
    ) {
      prev[RegionID].region = true;
    }
    return prev;
  }, {});
  const regionData = { regions: [], subregions: [], partialRegions: [] };
  Object.entries(processedLocations).forEach(
    ([RegionID, { subregions, partialRegion, region }]) => {
      if (region) {
        regionData.regions.push(parseInt(RegionID));
        return;
      }
      if (partialRegion) {
        regionData.partialRegions.push(parseInt(RegionID));
      }
      regionData.subregions.push(...subregions);
    }
  );
  if (regionData.regions.length === countries.length) {
    return null;
  }
  return regionData;
};
